import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox } from "@material-ui/core"
import ExpandMore from "@material-ui/icons/ExpandMore"
import { useEffect, useMemo } from "react"
import type { MouseEvent } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import {
  multiShareDeselectAllGroups,
  multiShareDeselectGroup,
  multiShareDeselectStudent,
  multiShareFetchGroups,
  multiShareSelectAllGroups,
  multiShareSelectGroup,
  multiShareSelectStudent
} from "../../../redux"
import type { AppState, AppThunkDispatch, Group, MultiShareActions, Student } from "../../../redux"
import { Body1, Body2, Header3, Header5, Header6 } from "../../atoms"

import "./multi-share-student-selector.styles.scss"

export interface IMultiShareStudentSelectorStateProps {
  groups: Group[]
  selectedGroups: Group[]
  fetchGroups: () => void
  selectAllGroups: (groups: Group[]) => void
  deselectAllGroups: () => void
  selectGroup: (group: Group) => void
  deselectGroup: (group: Group) => void
  selectStudent: (group: Group, student: Student) => void
  deselectStudent: (group: Group, student: Student) => void
}

export function MultiShareStudentSelectorView(props: IMultiShareStudentSelectorStateProps) {
  const {
    groups,
    selectedGroups,
    fetchGroups,
    selectAllGroups,
    deselectAllGroups,
    selectGroup,
    deselectGroup,
    selectStudent,
    deselectStudent
  } = props

  const { t } = useTranslation("multiShare")

  const isAllGroupsSelected = useMemo<boolean>(() => {
    return groups.length > 0 && selectedGroups.length === groups.length
  }, [selectedGroups, groups])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const isAnyGroupIndeterminate = useMemo<boolean>(() => {
    return groups.some((g: Group) => isGroupIndeterminate(g))
  }, [selectedGroups, groups])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  const isAllIndeterminate = useMemo<boolean>(() => {
    return (!isAllGroupsSelected && selectedGroups.length !== 0) || isAnyGroupIndeterminate
  }, [isAllGroupsSelected, selectedGroups])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchGroups()
  }, [])

  function findIndexById(array: (Group | Student)[], item: Group | Student): number {
    return array.findIndex((x: Group | Student) => x.id === item.id)
  }

  function isGroupSelected(group: Group): boolean {
    return findIndexById(selectedGroups, group) !== -1
  }

  function isGroupIndeterminate(group: Group): boolean {
    const index: number = findIndexById(selectedGroups, group)
    if (index === -1) return false
    if (group.students.length === selectedGroups[index].students.length) return false
    return true
  }

  function isStudentSelected(group: Group, student: Student): boolean {
    const groupIndex: number = findIndexById(selectedGroups, group)
    if (groupIndex === -1) return false
    return selectedGroups[groupIndex].students.findIndex((s) => s.id === student.id) !== -1
  }

  function onSelectGroup(group: Group): (event: MouseEvent) => void {
    return (event: MouseEvent) => {
      const selected: boolean = isGroupSelected(group)
      selected ? deselectGroup(group) : selectGroup(group)
      event.stopPropagation()
    }
  }

  function onSelectStudent(group: Group, student: Student): (event: MouseEvent) => void {
    return () => {
      const selected: boolean = isStudentSelected(group, student)
      selected ? deselectStudent(group, student) : selectStudent(group, student)
    }
  }

  function getStudentSelectionCount(group: Group): number {
    const index: number = findIndexById(selectedGroups, group)
    if (index === -1) return 0
    return selectedGroups[index].students.length
  }

  function handleSelectAllClick(): (event: MouseEvent) => void {
    isAllGroupsSelected ? deselectAllGroups() : selectAllGroups(groups)
    return (event: MouseEvent) => {
      event.stopPropagation()
    }
  }

  return (
    <Box className="multi-share-student-selector">
      <Header3>{t("selectShareWithStudents")}</Header3>
      <Accordion expanded={false}>
        <AccordionSummary>
          <Box className="multi-share-student-selector-accordion-summary">
            <Checkbox
              color="primary"
              checked={isAllGroupsSelected}
              indeterminate={isAllIndeterminate}
              onClick={handleSelectAllClick}
            />
            <Header6>{t("selectAllClasses")}</Header6>
          </Box>
        </AccordionSummary>
      </Accordion>
      {groups.map((group: Group, key: number) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in a later PR
        <Accordion key={key}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box className="multi-share-student-selector-accordion-summary">
              <Checkbox
                color="primary"
                checked={isGroupSelected(group)}
                indeterminate={isGroupIndeterminate(group)}
                onClick={onSelectGroup(group)}
              />
              <Body1>{group.name}</Body1>
              <Body2>
                {t("amountSelected", {
                  amount: getStudentSelectionCount(group),
                  totalAmount: group.students.length
                })}
              </Body2>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box className="multi-share-student-selector-accordion-details">
              <Header5>{t("selectStudents")}</Header5>
              <Box className="student-list">
                {group.students.map((student: Student, studentKey: number) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in a later PR
                  <Box className="student-cb" key={studentKey}>
                    <Checkbox
                      color="primary"
                      onClick={onSelectStudent(group, student)}
                      checked={isStudentSelected(group, student)}
                    />
                    <Body1>{student.name}</Body1>
                  </Box>
                ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

function mapStateToProps(state: AppState) {
  return {
    groups: state.multiShare.groups,
    selectedGroups: state.multiShare.selectedGroups
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<MultiShareActions>) {
  return {
    fetchGroups: () => dispatch(multiShareFetchGroups()),
    selectAllGroups: (groups: Group[]) => dispatch(multiShareSelectAllGroups(groups)),
    deselectAllGroups: () => dispatch(multiShareDeselectAllGroups()),
    selectGroup: (group: Group) => dispatch(multiShareSelectGroup(group)),
    deselectGroup: (group: Group) => dispatch(multiShareDeselectGroup(group)),
    selectStudent: (group: Group, student: Student) => dispatch(multiShareSelectStudent(group, student)),
    deselectStudent: (group: Group, student: Student) => dispatch(multiShareDeselectStudent(group, student))
  }
}

export const MultiShareStudentSelector = connect(mapStateToProps, mapDispatchToProps)(MultiShareStudentSelectorView)
